// ════════════════════════════════════════════════════════════════════════════
// 🛡️ CashierManager — Phase 4.3 Full-screen cashier management (#cashiers view)
// ════════════════════════════════════════════════════════════════════════════
// Manager-only view to:
//   - List all cashiers with role badges
//   - Create new cashier (username, password, name, role, optional PIN, permissions)
//   - Edit cashier (role, active status, reset password, set PIN, permissions)
//   - Deactivate/reactivate (soft-disable without delete)
// ════════════════════════════════════════════════════════════════════════════

import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  ArrowLeft, Users, Plus, Shield, User, Lock, Hash, Edit3,
  Trash2, Save, X, Loader2, CheckCircle2, AlertCircle,
  Power, PowerOff, Crown, Package, Eye, EyeOff,
  CheckSquare, Square, ChevronDown, ChevronRight, Settings2,
} from 'lucide-react';
import { Haptics } from '../../utils/haptics';
import { confirmAction } from './ConfirmModal';
import type { CashierSession } from './CashierAuth';

interface Cashier {
  id: number;
  name: string;
  username: string;
  role: 'manager' | 'cashier' | 'inventory' | 'readonly';
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  permissions?: string[] | null;
}

// 🎯 Permission catalog — all available actions grouped by category
const PERMISSIONS_CATALOG = [
  {
    name: 'Transactions', icon: '💰', color: 'emerald',
    permissions: [
      { key: 'transaction:checkout', label: 'Process Sales', desc: 'Create sales & checkout' },
      { key: 'transaction:discount', label: 'Apply Discounts', desc: 'Add discounts to sales' },
      { key: 'transaction:void', label: 'Void Transactions', desc: 'Cancel completed sales' },
      { key: 'transaction:view', label: 'View Transactions', desc: 'View transaction history' },
    ]
  },
  {
    name: 'Inventory', icon: '📦', color: 'amber',
    permissions: [
      { key: 'inventory:create', label: 'Add Products', desc: 'Create new products' },
      { key: 'inventory:edit', label: 'Edit Products', desc: 'Modify product details' },
      { key: 'inventory:delete', label: 'Delete Products', desc: 'Remove products' },
      { key: 'inventory:bulk', label: 'Bulk Operations', desc: 'Bulk import/export' },
    ]
  },
  {
    name: 'Customers', icon: '👥', color: 'cyan',
    permissions: [
      { key: 'customer:create', label: 'Add Customers', desc: 'Create customer records' },
      { key: 'customer:edit', label: 'Edit Customers', desc: 'Modify customer details' },
      { key: 'customer:delete', label: 'Delete Customers', desc: 'Remove customers' },
      { key: 'loyalty:config', label: 'Loyalty Settings', desc: 'Configure loyalty program' },
    ]
  },
  {
    name: 'Analytics & Reports', icon: '📊', color: 'violet',
    permissions: [
      { key: 'analytics:view', label: 'View Analytics', desc: 'Access analytics dashboard' },
      { key: 'dayend:run', label: 'Day-End Report', desc: 'Run end-of-day reports' },
    ]
  },
  {
    name: 'Store Management', icon: '⚙️', color: 'rose',
    permissions: [
      { key: 'store:settings', label: 'Store Settings', desc: 'Modify store config' },
      { key: 'store:theme', label: 'Theme Settings', desc: 'Change store appearance' },
    ]
  },
  {
    name: 'Cashier Management', icon: '🛡️', color: 'fuchsia',
    permissions: [
      { key: 'cashier:create', label: 'Add Cashiers', desc: 'Create cashier accounts' },
      { key: 'cashier:edit', label: 'Edit Cashiers', desc: 'Modify cashier details' },
      { key: 'cashier:delete', label: 'Remove Cashiers', desc: 'Delete cashier accounts' },
      { key: 'cashier:view', label: 'View Cashiers', desc: 'View cashier list' },
    ]
  },
  {
    name: 'Shifts', icon: '🕐', color: 'sky',
    permissions: [
      { key: 'shift:manage', label: 'Manage Shifts', desc: 'Start/end shifts' },
      { key: 'shift:view', label: 'View Shifts', desc: 'View shift history' },
    ]
  },
  {
    name: 'Hotel & Lodge', icon: '🏨', color: 'teal',
    permissions: [
      { key: 'hotel:read', label: 'View Rooms', desc: 'View room status & bookings' },
      { key: 'hotel:booking:create', label: 'Create Bookings', desc: 'New room bookings' },
      { key: 'hotel:booking:modify', label: 'Modify Bookings', desc: 'Edit booking details' },
      { key: 'hotel:booking:checkin', label: 'Check-In', desc: 'Guest check-in' },
      { key: 'hotel:booking:checkout', label: 'Check-Out', desc: 'Guest check-out' },
      { key: 'hotel:extras:add', label: 'Add Extras', desc: 'Add room charges/extras' },
      { key: 'hotel:extras:void', label: 'Void Extras', desc: 'Remove room charges' },
      { key: 'hotel:room:manage', label: 'Manage Rooms', desc: 'Add/edit rooms & types' },
      { key: 'hotel:reports', label: 'Hotel Reports', desc: 'Occupancy & revenue reports' },
    ]
  },
];

// Default permissions for each role
const ROLE_DEFAULT_PERMS: Record<string, string[]> = {
  cashier: ['transaction:checkout', 'transaction:discount', 'transaction:view', 'customer:create', 'customer:edit', 'analytics:view', 'shift:view'],
  inventory: ['inventory:create', 'inventory:edit', 'inventory:bulk', 'transaction:view', 'analytics:view', 'shift:view'],
  readonly: ['transaction:view', 'analytics:view', 'shift:view'],
};

// All permission keys (for Select All)
const ALL_PERM_KEYS = PERMISSIONS_CATALOG.flatMap(g => g.permissions.map(p => p.key));

const ROLE_COLORS: Record<Cashier['role'], string> = {
  manager: 'from-violet-500 to-fuchsia-500',
  cashier: 'from-emerald-500 to-cyan-500',
  inventory: 'from-amber-500 to-orange-500',
  readonly: 'from-slate-400 to-slate-500',
};

const ROLE_ICONS: Record<Cashier['role'], any> = {
  manager: Crown,
  cashier: User,
  inventory: Package,
  readonly: Eye,
};

const ROLE_LABELS: Record<Cashier['role'], string> = {
  manager: 'Manager',
  cashier: 'Cashier',
  inventory: 'Inventory',
  readonly: 'Read-only',
};

// ── Permission Selector Component ──
const PermissionSelector: React.FC<{
  role: Cashier['role'];
  selected: string[];
  onChange: (perms: string[]) => void;
}> = ({ role, selected, onChange }) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(PERMISSIONS_CATALOG.map(g => g.name)));

  const isManager = role === 'manager';
  const toggleGroup = (name: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  const togglePerm = (key: string) => {
    if (isManager) return; // Manager has all permissions
    onChange(selected.includes(key) ? selected.filter(p => p !== key) : [...selected, key]);
  };

  const toggleGroupAll = (groupPerms: { key: string }[]) => {
    if (isManager) return;
    const keys = groupPerms.map(p => p.key);
    const allSelected = keys.every(k => selected.includes(k));
    if (allSelected) {
      onChange(selected.filter(p => !keys.includes(p)));
    } else {
      onChange([...new Set([...selected, ...keys])]);
    }
  };

  const selectAll = () => onChange([...ALL_PERM_KEYS]);
  const clearAll = () => onChange([]);

  if (isManager) {
    return (
      <div className="rounded-xl bg-violet-50 border border-violet-200 p-3 text-center">
        <Crown size={16} className="mx-auto mb-1 text-violet-500" />
        <p className="text-[11px] font-bold text-violet-700">Manager has full access to all features</p>
        <p className="text-[9px] text-violet-500 mt-0.5">No individual permission selection needed</p>
      </div>
    );
  }

  const totalSelected = selected.length;
  const totalAvailable = ALL_PERM_KEYS.length;

  return (
    <div className="space-y-2">
      {/* Header bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Settings2 size={11} className="text-violet-500" />
          <span className="text-[10px] font-black uppercase tracking-wider text-slate-500">
            Permissions
          </span>
          <span className="text-[9px] font-bold text-violet-600 bg-violet-50 px-1.5 py-0.5 rounded-full">
            {totalSelected}/{totalAvailable}
          </span>
        </div>
        <div className="flex gap-1">
          <button type="button" onClick={selectAll} className="text-[8px] font-bold text-violet-600 hover:text-violet-800 px-1.5 py-0.5 rounded bg-violet-50 hover:bg-violet-100 transition-colors">
            Select All
          </button>
          <button type="button" onClick={clearAll} className="text-[8px] font-bold text-slate-500 hover:text-slate-700 px-1.5 py-0.5 rounded bg-slate-50 hover:bg-slate-100 transition-colors">
            Clear
          </button>
        </div>
      </div>

      {/* Permission groups */}
      <div className="max-h-[40vh] overflow-y-auto space-y-1.5 pr-1">
        {PERMISSIONS_CATALOG.map(group => {
          const groupKeys = group.permissions.map(p => p.key);
          const groupSelected = groupKeys.filter(k => selected.includes(k)).length;
          const allGroupSelected = groupKeys.every(k => selected.includes(k));
          const expanded = expandedGroups.has(group.name);

          return (
            <div key={group.name} className="rounded-xl border border-slate-200 bg-white overflow-hidden">
              {/* Group header */}
              <button
                type="button"
                onClick={() => toggleGroup(group.name)}
                className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-50 transition-colors"
              >
                <span className="text-[13px]">{group.icon}</span>
                <span className="text-[11px] font-black text-slate-700 flex-1 text-left">{group.name}</span>
                <span className="text-[8px] font-bold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-full">
                  {groupSelected}/{groupKeys.length}
                </span>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); toggleGroupAll(group.permissions); }}
                  className={`w-5 h-5 rounded flex items-center justify-center transition-colors ${
                    allGroupSelected
                      ? 'bg-violet-500 text-white'
                      : groupSelected > 0
                        ? 'bg-violet-100 text-violet-500 border border-violet-300'
                        : 'bg-slate-100 text-slate-400 border border-slate-200'
                  }`}
                  title={allGroupSelected ? 'Deselect all' : 'Select all'}
                >
                  {allGroupSelected ? <CheckSquare size={10} /> : groupSelected > 0 ? <Minus size={10} /> : <Square size={10} />}
                </button>
                {expanded ? <ChevronDown size={12} className="text-slate-400" /> : <ChevronRight size={12} className="text-slate-400" />}
              </button>

              {/* Permissions list */}
              <AnimatePresence>
                {expanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="overflow-hidden"
                  >
                    <div className="px-2 pb-2 space-y-0.5">
                      {group.permissions.map(perm => {
                        const isChecked = selected.includes(perm.key);
                        return (
                          <button
                            key={perm.key}
                            type="button"
                            onClick={() => togglePerm(perm.key)}
                            className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg transition-all text-left ${
                              isChecked
                                ? 'bg-violet-50 border border-violet-200'
                                : 'bg-white border border-transparent hover:bg-slate-50'
                            }`}
                          >
                            <div className={`w-4 h-4 rounded flex items-center justify-center shrink-0 transition-colors ${
                              isChecked ? 'bg-violet-500 text-white' : 'bg-slate-100 border border-slate-300'
                            }`}>
                              {isChecked && <CheckSquare size={10} />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-[10px] font-bold text-slate-700">{perm.label}</div>
                              <div className="text-[8px] text-slate-400">{perm.desc}</div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Minus icon for partial selection
const Minus: React.FC<{ size: number }> = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12"/></svg>
);

interface CashierManagerProps {
  storeApiKey: string;
  currentCashier: CashierSession | null;
  onBack: () => void;
}

const CashierManager: React.FC<CashierManagerProps> = ({ storeApiKey, currentCashier, onBack }) => {
  const [cashiers, setCashiers] = useState<Cashier[]>([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<Cashier | null>(null);

  // Form state
  const [form, setForm] = useState({
    name: '',
    username: '',
    password: '',
    role: 'cashier' as Cashier['role'],
    pin: '',
    isActive: true,
    permissions: [] as string[],
  });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const fetchCashiers = async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/store/cashiers`, {
        headers: { 'x-store-api-key': storeApiKey },
      });
      const data = await res.json();
      if (res.ok) setCashiers(data.cashiers || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchCashiers();
    // eslint-disable-next-line
  }, []);

  // 🎯 Auto-populate permissions when role changes
  const handleRoleChange = useCallback((newRole: Cashier['role']) => {
    setForm(f => ({
      ...f,
      role: newRole,
      permissions: newRole === 'manager' ? [] : (ROLE_DEFAULT_PERMS[newRole] || []),
    }));
  }, []);

  const createCashier = async () => {
    setFormError('');
    if (!form.name.trim() || !form.username.trim() || !form.password) {
      setFormError('Name, username, and password are required');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch('/api/store/cashiers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-store-api-key': storeApiKey,
        },
        body: JSON.stringify({
          storeApiKey,
          name: form.name.trim(),
          username: form.username.trim(),
          password: form.password,
          role: form.role,
          pin: form.pin || undefined,
          permissions: form.role === 'manager' ? null : (form.permissions.length > 0 ? form.permissions : null),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setFormError(data.error || 'Failed to create');
        Haptics.fire('warn');
        return;
      }
      Haptics.fire('success');
      setCreateOpen(false);
      setForm({ name: '', username: '', password: '', role: 'cashier', pin: '', isActive: true, permissions: ROLE_DEFAULT_PERMS['cashier'] });
      fetchCashiers();
    } catch (err: any) {
      setFormError(err?.message || 'Network error');
      Haptics.fire('warn');
    } finally {
      setSaving(false);
    }
  };

  const updateCashier = async () => {
    if (!editing) return;
    setFormError('');
    setSaving(true);
    try {
      const body: any = {};
      if (form.name !== undefined) body.name = form.name;
      if (form.role !== undefined) body.role = form.role;
      if (form.isActive !== undefined) body.isActive = form.isActive;
      if (form.password) body.password = form.password;
      if (form.pin !== undefined) body.pin = form.pin || null;
      // 🎯 Include permissions — null for manager (has everything), array for others
      body.permissions = form.role === 'manager' ? null : (form.permissions.length > 0 ? form.permissions : null);

      const res = await fetch(`/api/store/cashiers/${editing.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-store-api-key': storeApiKey,
        },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) {
        setFormError(data.error || 'Failed to update');
        Haptics.fire('warn');
        return;
      }
      Haptics.fire('success');
      setEditOpen(false);
      setEditing(null);
      setForm({ name: '', username: '', password: '', role: 'cashier', pin: '', isActive: true, permissions: [] });
      fetchCashiers();
    } catch (err: any) {
      setFormError(err?.message || 'Network error');
      Haptics.fire('warn');
    } finally {
      setSaving(false);
    }
  };

  const deleteCashier = async (c: Cashier) => {
    const ok = await confirmAction({
      title: `Remove ${c.name}?`,
      message: 'This cannot be undone. Their transaction history will be retained.',
      confirmLabel: 'Remove',
      destructive: true,
    });
    if (!ok) return;
    try {
      const res = await fetch(`/api/store/cashiers/${c.id}`, {
        method: 'DELETE',
        headers: { 'x-store-api-key': storeApiKey },
      });
      if (res.ok) {
        Haptics.fire('warn');
        fetchCashiers();
      }
    } catch { /* silent */ }
  };

  const beginEdit = (c: Cashier) => {
    setEditing(c);
    setForm({
      name: c.name,
      username: c.username,
      password: '',
      role: c.role,
      pin: '',
      isActive: c.isActive,
      permissions: c.permissions || (c.role === 'manager' ? [] : (ROLE_DEFAULT_PERMS[c.role] || [])),
    });
    setFormError('');
    setShowPassword(false);
    setEditOpen(true);
  };

  const isSelf = (c: Cashier) => currentCashier?.id === c.id;

  // Count active permissions for display
  const permCount = (c: Cashier) => {
    if (c.role === 'manager') return 'All';
    if (c.permissions && c.permissions.length > 0) return `${c.permissions.length}`;
    return `${(ROLE_DEFAULT_PERMS[c.role] || []).length}`;
  };

  return (
    <div className="fixed inset-0 z-[60] bg-gradient-to-br from-slate-50 via-white to-slate-100 flex flex-col">
      {/* Header */}
      <div className="shrink-0 flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-white/80 backdrop-blur-md">
        <div className="flex items-center gap-2">
          <button
            onClick={onBack}
            className="w-9 h-9 rounded-xl bg-slate-50 hover:bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-600 transition-colors"
          >
            <ArrowLeft size={15} />
          </button>
          <div>
            <h2 className="text-[15px] font-black text-slate-800 flex items-center gap-2">
              <Shield size={15} className="text-violet-500" /> Cashier Management
            </h2>
            <p className="text-[10px] text-slate-500">Roles, permissions & access</p>
          </div>
        </div>
        <motion.button
          whileTap={{ scale: 0.96 }}
          onClick={() => {
            setCreateOpen(true);
            setForm({ name: '', username: '', password: '', role: 'cashier', pin: '', isActive: true, permissions: ROLE_DEFAULT_PERMS['cashier'] });
            setFormError('');
            setShowPassword(false);
          }}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-violet-600 text-white text-[11px] font-black uppercase tracking-wider shadow-lg shadow-violet-500/20"
        >
          <Plus size={13} /> Add Cashier
        </motion.button>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {loading && (
          <div className="flex items-center justify-center py-12 text-slate-400 text-[11px]">
            <Loader2 size={14} className="animate-spin mr-2" /> Loading…
          </div>
        )}

        {!loading && cashiers.length === 0 && (
          <div className="text-center py-12 text-slate-400 text-[11px]">
            <Users size={28} className="mx-auto mb-2 text-slate-300" />
            No cashiers yet. Add your first team member.
          </div>
        )}

        {cashiers.map((c) => {
          const Icon = ROLE_ICONS[c.role];
          return (
            <motion.div
              key={c.id}
              whileTap={{ scale: 0.995 }}
              className={`rounded-2xl border p-3 bg-white ${c.isActive ? 'border-slate-200' : 'border-slate-200 opacity-60'}`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${ROLE_COLORS[c.role]} flex items-center justify-center text-white shrink-0`}>
                  <Icon size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-[12px] font-black text-slate-800 truncate">{c.name}</span>
                    {isSelf(c) && (
                      <span className="px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 text-[8px] font-black uppercase">You</span>
                    )}
                    {!c.isActive && (
                      <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 text-[8px] font-black uppercase">Inactive</span>
                    )}
                  </div>
                  <div className="text-[10px] text-slate-500 font-mono flex items-center gap-2 mt-0.5">
                    <span>@{c.username}</span>
                    <span>·</span>
                    <span className="capitalize">{ROLE_LABELS[c.role]}</span>
                    <span>·</span>
                    <span className="text-violet-500">{permCount(c)} perms</span>
                  </div>
                  {c.lastLoginAt && (
                    <div className="text-[9px] text-slate-400 mt-0.5">
                      Last login {new Date(c.lastLoginAt).toLocaleDateString('en-LK', { day: 'numeric', month: 'short' })}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => beginEdit(c)}
                    className="w-8 h-8 rounded-lg bg-slate-50 hover:bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-600 transition-colors"
                    title="Edit"
                  >
                    <Edit3 size={12} />
                  </button>
                  {!isSelf(c) && (
                    <button
                      onClick={() => deleteCashier(c)}
                      className="w-8 h-8 rounded-lg bg-rose-50 hover:bg-rose-100 border border-rose-200 flex items-center justify-center text-rose-500 transition-colors"
                      title="Remove"
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Create Modal */}
      <AnimatePresence>
        {createOpen && (
          <Modal onClose={() => setCreateOpen(false)} title="Add Cashier">
            <div className="space-y-3 p-1">
              <Field label="Full Name" icon={User}>
                <input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Roshan Silva"
                  className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 outline-none focus:border-violet-400 text-[13px]"
                />
              </Field>
              <Field label="Username" icon={User}>
                <input
                  value={form.username}
                  onChange={(e) => setForm((f) => ({ ...f, username: e.target.value.replace(/\s/g, '').toLowerCase() }))}
                  placeholder="roshan_s"
                  className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 outline-none focus:border-violet-400 text-[13px] font-mono"
                />
              </Field>
              <Field label="Password" icon={Lock}>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                    placeholder="••••••••"
                    className="w-full pl-3 pr-10 py-2 rounded-xl bg-slate-50 border border-slate-200 outline-none focus:border-violet-400 text-[13px]"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400"
                  >
                    {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </Field>
              <Field label="Role" icon={Shield}>
                <select
                  value={form.role}
                  onChange={(e) => handleRoleChange(e.target.value as Cashier['role'])}
                  className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 outline-none focus:border-violet-400 text-[13px]"
                >
                  <option value="cashier">Cashier — Process sales, discounts, customers</option>
                  <option value="inventory">Inventory — Manage products & stock</option>
                  <option value="readonly">Read-only — View reports only</option>
                  <option value="manager">Manager — Full access + void + settings</option>
                </select>
              </Field>
              <Field label="Quick PIN (optional)" icon={Hash}>
                <input
                  type="password"
                  inputMode="numeric"
                  value={form.pin}
                  onChange={(e) => setForm((f) => ({ ...f, pin: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
                  placeholder="4-6 digits for quick login"
                  className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 outline-none focus:border-violet-400 text-[13px] font-mono tracking-widest"
                />
              </Field>

              {/* 🎯 Permission Selector */}
              <PermissionSelector
                role={form.role}
                selected={form.permissions}
                onChange={(perms) => setForm(f => ({ ...f, permissions: perms }))}
              />

              {formError && (
                <div className="text-[11px] font-bold text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
                  {formError}
                </div>
              )}

              <div className="flex items-center gap-2 pt-1">
                <button
                  onClick={() => setCreateOpen(false)}
                  className="flex-1 py-2.5 rounded-xl bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-600 text-[11px] font-black uppercase"
                >
                  Cancel
                </button>
                <motion.button
                  whileTap={{ scale: 0.97 }}
                  onClick={createCashier}
                  disabled={saving}
                  className="flex-1 py-2.5 rounded-xl bg-violet-600 text-white text-[11px] font-black uppercase shadow-lg shadow-violet-500/20 disabled:opacity-50 flex items-center justify-center gap-1"
                >
                  {saving ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
                  Create
                </motion.button>
              </div>
            </div>
          </Modal>
        )}
      </AnimatePresence>

      {/* Edit Modal */}
      <AnimatePresence>
        {editOpen && editing && (
          <Modal onClose={() => setEditOpen(false)} title="Edit Cashier">
            <div className="space-y-3 p-1">
              {/* Read-only login username */}
              <div className="px-3 py-2 rounded-xl bg-slate-100 border border-slate-200 flex items-center gap-2">
                <span className="text-slate-400 text-[11px] font-bold uppercase tracking-wider shrink-0">Login Username:</span>
                <span className="text-slate-700 font-mono font-black text-[13px]">@{editing.username}</span>
              </div>
              <Field label="Full Name" icon={User}>
                <input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 outline-none focus:border-violet-400 text-[13px]"
                />
              </Field>
              <Field label="Role" icon={Shield}>
                <select
                  value={form.role}
                  onChange={(e) => handleRoleChange(e.target.value as Cashier['role'])}
                  className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 outline-none focus:border-violet-400 text-[13px]"
                >
                  <option value="cashier">Cashier</option>
                  <option value="inventory">Inventory</option>
                  <option value="readonly">Read-only</option>
                  <option value="manager">Manager</option>
                </select>
              </Field>
              <div className="flex items-center gap-2 py-1">
                <button
                  onClick={() => setForm((f) => ({ ...f, isActive: !f.isActive }))}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-xl border text-[11px] font-black uppercase transition-all ${
                    form.isActive
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-600'
                      : 'bg-slate-50 border-slate-200 text-slate-500'
                  }`}
                >
                  {form.isActive ? <Power size={12} /> : <PowerOff size={12} />}
                  {form.isActive ? 'Active' : 'Inactive'}
                </button>
              </div>
              <Field label="New Password (leave blank to keep)" icon={Lock}>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                    placeholder="••••••••"
                    className="w-full pl-3 pr-10 py-2 rounded-xl bg-slate-50 border border-slate-200 outline-none focus:border-violet-400 text-[13px]"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400"
                  >
                    {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </Field>
              <Field label='Quick PIN (leave blank to keep, "clear" to remove)' icon={Hash}>
                <input
                  type="password"
                  inputMode="numeric"
                  value={form.pin}
                  onChange={(e) => setForm((f) => ({ ...f, pin: e.target.value }))}
                  placeholder="4-6 digits"
                  className="w-full px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 outline-none focus:border-violet-400 text-[13px] font-mono tracking-widest"
                />
              </Field>

              {/* 🎯 Permission Selector */}
              <PermissionSelector
                role={form.role}
                selected={form.permissions}
                onChange={(perms) => setForm(f => ({ ...f, permissions: perms }))}
              />

              {formError && (
                <div className="text-[11px] font-bold text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
                  {formError}
                </div>
              )}

              <div className="flex items-center gap-2 pt-1">
                <button
                  onClick={() => setEditOpen(false)}
                  className="flex-1 py-2.5 rounded-xl bg-slate-50 hover:bg-slate-100 border border-slate-200 text-slate-600 text-[11px] font-black uppercase"
                >
                  Cancel
                </button>
                <motion.button
                  whileTap={{ scale: 0.97 }}
                  onClick={updateCashier}
                  disabled={saving}
                  className="flex-1 py-2.5 rounded-xl bg-violet-600 text-white text-[11px] font-black uppercase shadow-lg shadow-violet-500/20 disabled:opacity-50 flex items-center justify-center gap-1"
                >
                  {saving ? <Loader2 size={11} className="animate-spin" /> : <Save size={11} />}
                  Save
                </motion.button>
              </div>
            </div>
          </Modal>
        )}
      </AnimatePresence>
    </div>
  );
};

const Field: React.FC<{ label: string; icon: any; children: React.ReactNode }> = ({ label, icon: Icon, children }) => (
  <div className="space-y-1">
    <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
      <Icon size={10} /> {label}
    </label>
    {children}
  </div>
);

const Modal: React.FC<{ onClose: () => void; title: string; children: React.ReactNode }> = ({ onClose, title, children }) => (
  <motion.div
    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    className="fixed inset-0 z-[9998] bg-slate-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
    onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
  >
    <motion.div
      initial={{ y: 400 }} animate={{ y: 0 }} exit={{ y: 400 }}
      transition={{ type: 'spring', stiffness: 380, damping: 32 }}
      className="w-full sm:max-w-md max-h-[92vh] bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col"
    >
      <div className="shrink-0 px-5 pt-4 pb-3 border-b border-slate-100 flex items-center justify-between">
        <h3 className="text-[14px] font-black text-slate-800">{title}</h3>
        <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-700">
          <X size={15} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-4">{children}</div>
    </motion.div>
  </motion.div>
);

export default CashierManager;
